import pino from 'pino';
import pinoHttp from 'pino-http';

const isValidEmailFormat = (email) => {
    const regex = /^.*@\w+\.\w+.*$/;
    return regex.test(email);
};
const isValidPhoneFormat = (number) => {
    if (!number.match(/^((09|03|07|08|05)+([0-9]{8}))$/) &&
        !number.match(/^(\+(84)+(09|03|07|08|05)+([0-9]{8}))$/) &&
        !number.match(/^(\+(84)+(9|3|7|8|5)+([0-9]{8}))$/) &&
        !number.match(/^((84)+(09|03|07|08|05)+([0-9]{8}))$/) &&
        !number.match(/^((84)+(9|3|7|8|5)+([0-9]{8}))$/)) {
        return false;
    }
    return true;
};
const PATTERN_VALIDATION = {
    email: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
    phone: /^([+84|84|0]+(3|5|7|8|9|1[2|6|8|9]))+([0-9]{8})\b$/,
    vietnameseName: /^[a-zA-ZÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂẾưăạảấầẩẫậắằẳẵặẹẻẽềềểếỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵýỷỹ\s\w|_]+$/,
    domain: /^([a-z0-9]([a-z0-9\-]{0,61}[a-z0-9]|[a-z0-9]{0,62})\.){2,}[a-z]{2,63}$/i,
};

class ApiError {
    constructor(message, code, response) {
        this.message = message;
        this.response = response;
        this.code = code;
    }
    get reason() {
        return this.response;
    }
}

const normalizeSlashUrl = (baseURL = '', url) => {
    const normalizedBaseUrl = baseURL.endsWith('/')
        ? baseURL.slice(0, baseURL.length - 1)
        : baseURL;
    const normalizedPath = url.startsWith('/') ? url.slice(1) : url;
    return `${normalizedBaseUrl}/${normalizedPath}`;
};
const DEFAULT_ERROR_MESSAGE = 'Đã có lỗi xảy ra, vui lòng thử lại sau!';
class Api {
    constructor(url, getError, defaultConfig = {}) {
        this.baseURL = url;
        this.interceptors = [];
        this.getError = getError;
        this.defaultConfig = defaultConfig;
    }
    _parseJson(response) {
        return response.text().then((text) => (text ? JSON.parse(text) : null));
    }
    addInterceptor(interceptFn) {
        this.interceptors.push(interceptFn);
    }
    appendParamsToURL(url, params = {}) {
        if (params) {
            const paramsArr = [];
            for (const k of Object.keys(params)) {
                if (params[k] !== null && params[k] !== undefined) {
                    paramsArr.push(`${k}=${encodeURIComponent(params[k])}`);
                }
            }
            if (paramsArr.length > 0)
                url = `${url}?${paramsArr.join('&')}`;
        }
        return url;
    }
    async request(url, body, method, config = {}) {
        var _a;
        let headers = config.headers || {};
        url = this.appendParamsToURL(url, config.params);
        if (body) {
            if (typeof body !== 'string') {
                if ('append' in body) ;
                else {
                    body = JSON.stringify(body);
                    headers = Object.assign({ 'Content-Type': 'application/json' }, headers);
                }
            }
        }
        const requestObj = Object.assign(Object.assign({}, this.defaultConfig), { method: method, headers: new Headers(headers), body });
        this.interceptors.forEach((fn) => fn(requestObj));
        const res = await fetch(url.startsWith('http') ? url : normalizeSlashUrl(this.baseURL, url), requestObj);
        if (res.status >= 400) {
            const resp = await res.json();
            const error = ((_a = this.getError) === null || _a === void 0 ? void 0 : _a.call(this, resp)) || new ApiError(DEFAULT_ERROR_MESSAGE, -1, resp);
            return Promise.reject(error);
        }
        const respHeaders = {};
        res.headers.forEach((val, key) => (respHeaders[key] = val));
        const data = await this._parseJson(res);
        return { code: res.status, data, headers: respHeaders };
    }
    async get(url, config = {}) {
        return await this.request(url, null, 'GET', config);
    }
    async post(url, body, config = {}) {
        return await this.request(url, body, 'POST', config);
    }
    async patch(url, body, config = {}) {
        return await this.request(url, body, 'PATCH', config);
    }
    async put(url, body, config = {}) {
        return await this.request(url, body, 'PUT', config);
    }
    async delete(url, body = {}, config = {}) {
        return await this.request(url, body, 'DELETE', config);
    }
}

const logger = pino({
    prettyPrint: process.env.NODE_ENV === 'development',
    level: process.env.LOG_LEVEL || 'info',
    serializers: {
        err: pino.stdSerializers.err,
        req: pino.stdSerializers.req,
    },
    browser: {
        transmit: {
            level: 'error',
            send: (_level, logEvent) => {
                if (typeof window !== 'undefined' && window.track) {
                    window.track('error', {
                        errorSource: 'client',
                        errorCode: 'unknown',
                        errorMessage: JSON.stringify(logEvent),
                    });
                }
            },
        },
    },
});
const getHttpLogger = (opts) => {
    const { customProps } = opts || {};
    return pinoHttp({
        logger,
        customLogLevel: function (res, err) {
            if (res.statusCode >= 400 && res.statusCode < 500) {
                return 'warn';
            }
            else if (res.statusCode >= 500 || err) {
                return 'error';
            }
            return 'debug';
        },
        autoLogging: true,
        customProps,
    });
};

const formatCurrencyVND = (value, includeUnit = true) => {
    if (typeof value !== 'number')
        return null;
    let currencyFormat;
    if (includeUnit)
        currencyFormat = new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        });
    else
        currencyFormat = new Intl.NumberFormat('vi-VN');
    return currencyFormat.format(value);
};

export { Api, ApiError, PATTERN_VALIDATION, formatCurrencyVND, getHttpLogger, isValidEmailFormat, isValidPhoneFormat, logger };
